import { JSX } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PinIcon } from '@dynatrace/strato-icons';

import type { ColumnListItem } from './column-settings-types.js';
import { Button } from '../../../../../buttons/button/Button.js';
import { Menu } from '../../../../../navigation/menu/Menu.js';
import { Tooltip } from '../../../../../overlays/tooltip/Tooltip.js';
import { pinColumns } from '../../../features/ColumnPinning/column-pinning-utils.js';
import { dataTableMessages } from '../../../messages.js';
import type {
  ColumnPinningPosition,
  ColumnPinningState,
} from '../../../tanstack-table-core-override/src/index.js';

interface ColumnPinningMenuProps {
  /**
   * The column to render the pinning menu for.
   */
  column: ColumnListItem;
  /**
   * The ordered flat columns, grouped by pinning position.
   */
  orderedFlatColumns: {
    left: ColumnListItem[];
    center: ColumnListItem[];
    right: ColumnListItem[];
  };
  /**
   * The column pinning state in the settings modal.
   */
  localColumnPinning: ColumnPinningState;
  /**
   * Callback to update the pinning state in the settings modal.
   */
  updateLocalColumnPinning: (newState: ColumnPinningState) => void;
}

/**
 * Menu component for pinning/unpinning columns in the DataTable column settings.
 * @internal
 */
export const ColumnPinningMenu = (
  props: ColumnPinningMenuProps,
): JSX.Element => {
  const {
    column,
    orderedFlatColumns,
    localColumnPinning,
    updateLocalColumnPinning,
  } = props;

  const intl = useIntl();
  const columnId = column.id;
  const pinnedPosition = column.pinned;
  const pinningLabel = intl.formatMessage(dataTableMessages.pinColumnLabel, {
    isUnpinned: pinnedPosition === false,
  });

  /**
   * Returns all columns that belong to the same pinning region as the current column.
   * If the current column is not pinned, the function returns the unpinned ("center") columns.
   *
   * @returns An array of columns from the same pinning region (left, right, or unpinned).
   */
  function getColumnsInSamePinningRegion(): ColumnListItem[] {
    const position = pinnedPosition === false ? 'center' : pinnedPosition;
    return orderedFlatColumns[position];
  }

  /**
   * Returns the ID of the current column or, if it has nested columns,
   * the IDs of its nested child columns in the same pinning region (left, right, or unpinned).
   *
   * @returns An array of column IDs that should be pinned or unpinned together.
   */
  function getIdsToPin(): string[] {
    const hasNestedColumns = column.columns.length;
    if (!hasNestedColumns) {
      return [columnId];
    }

    const idsToPin: string[] = [];
    const columns = getColumnsInSamePinningRegion();

    // find parent column
    // use key instead of ID, as the same parent (with same ID) can appear multiple times,
    // even within the same pinned area
    const startIndex = columns.findIndex((col) => col.key === column.key);
    if (startIndex < 0) {
      return [];
    }
    // get first nested child
    let index = startIndex + 1;
    let current = columns.at(index);
    // collect all adjacent children
    while (current?.parent?.id === columnId) {
      idsToPin.push(current.id);
      index++;
      current = columns.at(index);
    }
    return idsToPin;
  }

  const pin = (position: ColumnPinningPosition): void => {
    const idsToPin = getIdsToPin();
    const newState = pinColumns(idsToPin, localColumnPinning, position);
    updateLocalColumnPinning(newState);
  };

  return (
    <Tooltip text={pinningLabel} placement="right">
      <Menu>
        <Menu.Trigger>
          <Button size="condensed" aria-label={pinningLabel}>
            <Button.Prefix>
              <PinIcon />
            </Button.Prefix>
          </Button>
        </Menu.Trigger>
        <Menu.Content>
          {pinnedPosition !== 'left' && (
            <Menu.Item
              onSelect={() => pin('left')}
              data-dt-name="ColumnSettingsPinLeft"
            >
              <FormattedMessage {...dataTableMessages.pinColumnLeftLabel} />
            </Menu.Item>
          )}
          {pinnedPosition !== 'right' && (
            <Menu.Item
              onSelect={() => pin('right')}
              data-dt-name="ColumnSettingsPinRight"
            >
              <FormattedMessage {...dataTableMessages.pinColumnRightLabel} />
            </Menu.Item>
          )}
          {pinnedPosition !== false && (
            <Menu.Item
              onSelect={() => pin(false)}
              data-dt-name="ColumnSettingsUnpin"
            >
              <FormattedMessage {...dataTableMessages.unpinColumnLabel} />
            </Menu.Item>
          )}
        </Menu.Content>
      </Menu>
    </Tooltip>
  );
};
